import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { UserRoles, UserRolesEnum, UserType } from "../Constant";
import { useQueryClient } from "@tanstack/react-query";
import { tokenManager } from "../Utils";

interface AuthContextType {
  user: UserType | null;
  userPermission: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (data: any) => void;
  signOut: () => void;
  navigationPath: any;
  checkUserPermission: (feature: string) => boolean;
  isAdmin: boolean;
  isSystemAdmin: boolean;
}

const defaultContext: AuthContextType = {
  user: null,
  userPermission: null,
  isAuthenticated: false,
  navigationPath: "none",

  isLoading: true,
  signIn: () => {},
  signOut: () => {},
  checkUserPermission: () => false,
  isAdmin: false,
  isSystemAdmin: false,
};

const AuthContext = createContext<AuthContextType>(defaultContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [userPermission, setUserPermission] = useState<any | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [navigationPath, setNavigationPath] = useState<"none" | "system">(
    "none",
  );
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setIsLoading(true);
    const isAuth = localStorage.getItem("isAuth");
    const userId = localStorage.getItem("userId");
    const username = localStorage.getItem("username");
    const storedRole = localStorage.getItem("userRole") as UserRoles | null;
    const permission = localStorage.getItem("userPermission");
    const token = tokenManager.getToken();

    if (isAuth && storedRole && token) {
      const userRole: UserRoles = Object.values(UserRolesEnum).includes(
        storedRole as UserRolesEnum,
      )
        ? (storedRole as UserRoles)
        : UserRolesEnum.none;

      const newUser: UserType = {
        userId: userId || "",
        username: username || "",
        userRole,
      };

      setUser(newUser);
      setIsAuthenticated(true);
      setUserPermission(permission ? JSON.parse(permission) : []);
    } else {
      signOut();
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (user?.userRole && user.userRole !== UserRolesEnum.none) {
      setNavigationPath("system");
    } else {
      setNavigationPath("none");
    }

    if (
      user?.userRole === UserRolesEnum.admin ||
      user?.userRole === UserRolesEnum.Admin
    ) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }

    setIsSystemAdmin(user?.userRole === UserRolesEnum.SystemAdmin);
  }, [user]);

  const signOut = useCallback(() => {
    const isDarkMod = localStorage.getItem("isDarkMod");
    const theme = localStorage.getItem("theme");

    localStorage.clear();
    tokenManager.clearToken();
    queryClient.clear();

    if (isDarkMod !== null) {
      localStorage.setItem("isDarkMod", isDarkMod);
    }
    if (theme !== null) {
      localStorage.setItem("theme", theme);
    }

    setUser(null);
    setIsAuthenticated(false);
    setUserPermission(null);
  }, [queryClient]);

  const signIn = useCallback((data: any) => {
    const { isAuthDone, userData, userPermission, auth_token } = data;

    localStorage.setItem("isAuth", String(isAuthDone));
    localStorage.setItem("userId", userData?.userId);
    localStorage.setItem("username", userData?.username);
    localStorage.setItem("userRole", userData?.userRole);
    localStorage.setItem("userPermission", JSON.stringify(userPermission));
    if (auth_token) {
      tokenManager.setToken(auth_token);
    }

    setUser({
      userId: userData?.userId,
      username: userData?.username,
      userRole: userData?.userRole,
    });

    if (
      userData?.userRole === UserRolesEnum.admin ||
      userData?.userRole === UserRolesEnum.Admin
    ) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }

    setIsSystemAdmin(userData?.userRole === UserRolesEnum.SystemAdmin);

    setIsAuthenticated(true);
    setUserPermission(userPermission);
  }, []);

  const checkUserPermission = useCallback(
    (feature: string): boolean => {
      if (
        user?.userRole === UserRolesEnum.admin ||
        user?.userRole === UserRolesEnum.Admin
      ) {
        return true;
      }

      if (!userPermission || !Array.isArray(userPermission)) return false;

      if (
        userPermission.length === 0 ||
        typeof userPermission[0] === "string"
      ) {
        return (userPermission as string[]).includes(feature);
      }

      return userPermission.some((category) =>
        category.features?.some(
          (perm: { navigation: string; is_locked?: number }) =>
            perm.navigation === feature && perm.is_locked === 0,
        ),
      );
    },
    [userPermission, user],
  );

  return (
      <AuthContext.Provider
        value={{
          user,
          navigationPath,
          userPermission,
          isAuthenticated,
          isLoading,
          signIn,
          signOut,
          isAdmin,
          isSystemAdmin,
          checkUserPermission,
        }}
      >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthChecker = () => useContext(AuthContext);
